import lib from "./lib";

/**
 * OSS上传相关工具函数
 */
class OssUtils {
    /**
     * 获取OSS上传签名
     * @param {Object} params - 参数对象
     * @param {string} params.bucket - OSS bucket名称，默认为 "dante-img"
     * @param {string} params.region - OSS region，默认为 "cn-hangzhou"
     * @param {number} params.maxFileSize - 最大文件大小，默认为 10MB
     * @param {string} params.uploadDir - 上传目录/key
     * @returns {Promise} 返回签名信息
     */
    static async getOssSignature({
        bucket = "dante-img",
        region = "cn-hangzhou",
        maxFileSize = 10485760, // 10MB
        uploadDir,
    }) {
        return new Promise((resolve, reject) => {
            lib.request({
                url: "/park-gate/oss/post-signature",
                data: {
                    bucket,
                    region,
                    maxFileSize,
                    uploadDir,
                },
                success: data => {
                    resolve(data);
                },
                fail: (code, msg, data) => {
                    reject({ code, msg, data });
                },
                needMask: false,
            });
        });
    }

    /**
     * 生成OSS上传的key
     * @param {string} fileName - 文件名
     * @param {string} prefix - 前缀，可选
     * @returns {string} 生成的key
     */
    static generateOssKey(fileName, prefix = "") {
        const suffix = fileName.split(".").reverse()[0].toLowerCase();
        const timestamp = new Date().getTime() % 100000000;
        const random = parseInt(Math.random() * 1000);
        const key = `${timestamp}${random}.${suffix}`;

        return prefix ? `${prefix}/${key}` : key;
    }

    /**
     * 生成数字字符串或UUID
     * @param {boolean} notUuid - 是否生成数字字符串（true）还是UUID（false）
     * @param {number} length - 当notUuid为true时，生成数字字符串的长度
     * @returns {string} 生成的字符串
     */
    static generateNumberString(notUuid, length) {
        let n = Date.now();

        if (notUuid) {
            return Array.from({ length: length }, (_, i) => {
                if (i === 0 || i === length - 1) {
                    return "9";
                } else {
                    n = Math.floor(n / 10);
                    return ((n + 10 * Math.random()) % 10 | 0).toString();
                }
            }).join("");
        } else {
            return "xxxxxxxxxxxx-xxxxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (e) {
                let t = (n + 16 * Math.random()) % 16 | 0;
                n = Math.floor(n / 16);
                return ("x" === e ? t : (3 & t) | 8).toString(16);
            });
        }
    }

    /**
     * 生成带前缀的OSS key，使用自定义数字字符串
     * @param {string} fileName - 文件名
     * @param {string} prefix - 前缀，可选
     * @param {boolean} useNumberString - 是否使用数字字符串，默认true
     * @param {number} numberLength - 数字字符串长度，默认18
     * @returns {string} 生成的key
     */
    static generateOssKeyWithNumber(fileName, prefix = "", useNumberString = true, numberLength = 18) {
        const suffix = fileName.split(".").reverse()[0].toLowerCase();
        let identifier;
        const timestamp = new Date().getTime() % 100000000;
        const random = parseInt(Math.random() * 1000);
        const timestampRandom = `${timestamp}${random}`;
        if (useNumberString) {
            identifier = this.generateNumberString(false, numberLength) + "_" + timestampRandom;
        } else {
            identifier = timestampRandom;
        }

        const key = `${identifier}.${suffix}`;
        return prefix ? `${prefix}/${key}` : key;
    }

    /**
     * 处理上传URL，自动添加协议
     * @param {string} host - 主机地址
     * @returns {string} 完整的上传URL
     */
    static processUploadUrl(host) {
        return host.startsWith("http") ? host : `https://${host}`;
    }

    /**
     * 构建OSS上传的FormData
     * @param {Object} signatureData - 签名数据
     * @param {string} key - 文件key
     * @param {File} file - 文件对象
     * @returns {FormData} 构建好的FormData
     */
    static buildUploadFormData(signatureData, key, file) {
        const formData = new FormData();
        formData.append("key", key);
        formData.append("success_action_status", "200");
        formData.append("policy", signatureData.policy);
        formData.append("x-oss-signature", signatureData.signature);
        formData.append("x-oss-signature-version", signatureData.version);
        formData.append("x-oss-credential", signatureData.xOssCredential);
        formData.append("x-oss-date", signatureData.xOssDate);
        formData.append("x-oss-security-token", signatureData.securityToken);
        formData.append("file", file);
        return formData;
    }

    /**
     * 为文件对象添加OSS签名信息
     * @param {Object} file - 文件对象
     * @param {Object} signatureData - 签名数据
     * @param {string} key - 文件key
     * @param {string} uploadUrl - 上传URL
     */
    static attachSignatureToFile(file, signatureData, key, uploadUrl) {
        file.key = key;
        file.url = `${uploadUrl}/${key}`;
        file.policy = signatureData.policy;
        file.signature = signatureData.signature;
        file.version = signatureData.version;
        file.xOssCredential = signatureData.xOssCredential;
        file.xOssDate = signatureData.xOssDate;
        file.securityToken = signatureData.securityToken;
        file.uploadUrl = uploadUrl;
    }
}

export default OssUtils;
